import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { type ReactNode, useEffect, useSyncExternalStore } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/useTranslation';
import { ACCOUNT_PENDING_PATH, getAccountApproval, subscribeAccountApproval } from '../../lib/accountApproval';
import { isIdleSignedOut, registerProtectedPage, subscribeIdleSignedOut } from '../../lib/idleSession';
import { loginPathWithRedirect } from '../../lib/redirectPath';
import './ProtectedRoute.css';

interface ProtectedRouteProps {
  children?: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { t } = useTranslation();
  const { state } = useAuth();
  const { isAuthenticated, isLoading } = state;
  // Closed beta: signed out because the account is not approved (yet).
  const approval = useSyncExternalStore(subscribeAccountApproval, getAccountApproval, getAccountApproval);
  // Signed out for inactivity: come back to this page after signing in again.
  const idleSignedOut = useSyncExternalStore(subscribeIdleSignedOut, isIdleSignedOut, isIdleSignedOut);
  const location = useLocation();

  // The idle warning and the timed sign-out run only on authenticated pages
  // (IdleSessionGuard), never on login, register or the public pages.
  useEffect(() => (isAuthenticated ? registerProtectedPage() : undefined), [isAuthenticated]);

  if (isLoading) {
    return (
      <div
        className="protected-route__loading"
        aria-live="polite"
        aria-label={t('auth.loadingStatus')}
      >
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (approval !== null) return <Navigate to={ACCOUNT_PENDING_PATH} replace />;
    const loginPath = idleSignedOut ? loginPathWithRedirect(`${location.pathname}${location.search}`) : '/login';
    return <Navigate to={loginPath} replace />;
  }

  return children !== undefined ? <>{children}</> : <Outlet />;
}

export default ProtectedRoute;
